import snakeCase from 'lodash/snakeCase';

import { ensureValue } from './typeUtils';

export const downloadBlob = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.style.opacity = '0';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const downloadFromUrl = (url: string, fileName: string) =>
  fetch(url)
    .then((response) => response.blob())
    .then((blob) => downloadBlob(blob, fileName));

export const openLinkInNewTab = (url: string, name: string) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.target = '_blank';
  document.body.appendChild(link);
};

export const bulkDownloadFromUrl = async ({
  files,
  fileName,
}: {
  files: { url: string; fileName: string }[];
  fileName: string;
}) => {
  const { default: JSZip } = await import('jszip');

  const zip = new JSZip();

  const filePromises = files.map(async (file) => {
    try {
      const response = await fetch(ensureValue(file.url));
      const blob = await response.blob();
      zip.file(`${file.fileName}`, blob);
    } catch (error) {
      console.error(`Error fetching file ${file.fileName}:`, error);
    }
  });

  // Wait for all files to be added to the zip
  await Promise.all(filePromises);

  // Generate the zip file
  const zipBlob = await zip.generateAsync({ type: 'blob' });

  downloadBlob(zipBlob, `${fileName}.zip`);
};

export async function uploadFiles(
  files: File[],
  options: {
    onProgress?: (progress: number) => void;
    onFinish?: () => void;
    createFile: (params: {
      fileName: string;
      fileSize: number;
      mimeType: string;
    }) => Promise<{ uploadUrl: string; file: { id: string } }>;
    confirmUpload: (params: { fileId: string }) => void | Promise<unknown>;
    userId: string;
  },
) {
  const { onProgress, onFinish, createFile, confirmUpload, userId } = options;

  const totalBytes = files.reduce((acc, file) => acc + file.size, 0);
  const fileProgress = new Map<string, number>();

  // Initialize progress tracking for each file
  files.forEach((file, index) => {
    fileProgress.set(index.toString(), 0);
  });

  const updateTotalProgress = () => {
    const totalUploaded = Array.from(fileProgress.values()).reduce(
      (sum, progress) => sum + progress,
      0,
    );
    const totalProgressPercent = Math.round((totalUploaded / totalBytes) * 100);
    onProgress?.(totalProgressPercent);
  };

  await Promise.all(
    files.map(async (file, index) => {
      const fileIndex = index.toString();
      const [, name, extension] = file.name.match(/^(.+?)\.([a-z0-9]+)$/) ?? [];

      const prefix = Date.now().toString(36);

      const { uploadUrl, file: createdFile } = await createFile({
        fileName: `${prefix}_${snakeCase(name)}.${extension}`,
        fileSize: file.size,
        mimeType: file.type,
      });

      // Upload file using XMLHttpRequest for progress tracking
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            // Update this file's progress
            fileProgress.set(fileIndex, event.loaded);
            updateTotalProgress();
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            // Ensure file is marked as fully uploaded
            fileProgress.set(fileIndex, file.size);
            updateTotalProgress();
            resolve();
          } else {
            reject(new Error(`Failed to upload file: ${xhr.statusText}`));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Upload failed'));
        });

        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.setRequestHeader('x-amz-meta-fileId', createdFile.id);
        xhr.setRequestHeader('x-amz-meta-uploaderId', userId);

        xhr.send(file);
      });

      // Confirm upload completion
      await confirmUpload({
        fileId: createdFile.id,
      });
    }),
  );

  onFinish?.();
}

export async function printPdf(url: string) {
  const response = await fetch(url);
  const blob = await response.blob();

  const objectUrl = URL.createObjectURL(blob);

  const iframe = document.createElement('iframe');
  const iframeRemovalTimeout = setTimeout(() => {
    document.body.removeChild(iframe);
    URL.revokeObjectURL(objectUrl);
  }, 20_000);

  iframe.onload = () => {
    const win = iframe.contentWindow;

    win?.addEventListener(
      'afterprint',
      () => {
        clearTimeout(iframeRemovalTimeout);
        document.body.removeChild(iframe);
      },
      { once: true },
    );

    win?.print();
  };

  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.opacity = '0';
  iframe.style.position = 'absolute';
  iframe.style.top = '0';
  iframe.style.left = '0';

  iframe.src = objectUrl;

  document.body.appendChild(iframe);
}
